import React from 'react';
import { colors, fontFamily, severity, space, typeScale } from '../data/designTokens';
import type { Severity } from '../data/designTokens';

const CHART_SERIES = [colors.brand, '#1E3A8A', '#B45309', '#7C2D12', '#374151'];

// ─── Stat Card with delta ─────────────────────────────────────

export type StatItem = {
    label: string;
    value: string;
    delta?: string;
    deltaTone?: 'pos' | 'neg' | 'neutral';
    sub?: string;
};

export const ScreenStat: React.FC<StatItem> = ({ label, value, delta, deltaTone = 'neutral', sub }) => (
    <div
        style={{
            flex: '1 1 120px',
            padding: 16,
            borderRadius: 6,
            border: `1px solid ${colors.hairline}`,
            background: colors.surface,
        }}
    >
        <div
            style={{
                fontSize: 10,
                color: colors.muted,
                letterSpacing: 1,
                textTransform: 'uppercase',
                marginBottom: 6,
                fontWeight: 600,
            }}
        >
            {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, color: colors.ink, lineHeight: 1.1 }}>{value}</div>
        {delta ? (
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    marginTop: 4,
                    color: deltaTone === 'pos' ? colors.pos : deltaTone === 'neg' ? colors.neg : colors.muted,
                }}
            >
                {delta}
            </div>
        ) : null}
        {sub ? <div style={{ fontSize: 10, color: colors.mutedSoft, marginTop: 2 }}>{sub}</div> : null}
    </div>
);

export const ScreenStatGrid: React.FC<{ items: StatItem[] }> = ({ items }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
        {items.map((item, i) => (
            <ScreenStat key={i} {...item} />
        ))}
    </div>
);

// ─── Insights Triad (Observations / Recommendations / Risks) ─────

export type TriadProps = {
    observations: string[];
    recommendations: string[];
    risks: string[];
    max?: number;
    label?: string;
};

const bulletList = (items: string[], tone: string, tint: string) => ({ items, tone, tint });

const ScreenTriadColumn: React.FC<{ label: string; items: string[]; accent: string; tint: string }> = ({ label, items, accent, tint }) => (
    <div
        style={{
            flex: 1,
            padding: 12,
            borderLeft: `2px solid ${accent}`,
            background: tint,
            borderRadius: 4,
        }}
    >
        <div
            style={{
                fontSize: 10,
                letterSpacing: 1,
                textTransform: 'uppercase',
                fontWeight: 700,
                color: accent,
                marginBottom: 6,
            }}
        >
            {label}
        </div>
        {items.length === 0 ? (
            <div style={{ fontSize: 11, color: colors.mutedSoft }}>—</div>
        ) : (
            items.map((it, i) => (
                <div key={i} style={{ display: 'flex', marginBottom: 4 }}>
                    <span style={{ width: 12, color: accent }}>•</span>
                    <span style={{ flex: 1, fontSize: 11.5, color: colors.inkSoft, lineHeight: 1.45 }}>{it}</span>
                </div>
            ))
        )}
    </div>
);

export const ScreenInsightsTriad: React.FC<TriadProps> = ({ observations, recommendations, risks, max = 6 }) => (
    <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
        <ScreenTriadColumn label="Observations" items={observations.slice(0, max)} accent={colors.info} tint={colors.infoSoft} />
        <ScreenTriadColumn label="Recommendations" items={recommendations.slice(0, max)} accent={colors.brand} tint={colors.brandSoft} />
        <ScreenTriadColumn label="Risks" items={risks.slice(0, max)} accent={colors.neg} tint={colors.negSoft} />
    </div>
);

// ─── Horizontal bar list ────────────────────────────────────────

export type BarItem = { label: string; value: number; valueLabel?: string };

export const ScreenMiniBars: React.FC<{ items: BarItem[] }> = ({ items }) => {
    const max = Math.max(1, ...items.map((i) => i.value));
    return (
        <div style={{ marginTop: 8 }}>
            {items.map((item, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: colors.inkSoft }}>{item.label}</span>
                        <span style={{ fontSize: 11, color: colors.muted, fontWeight: 600 }}>
                            {item.valueLabel ?? item.value.toLocaleString('en-IN')}
                        </span>
                    </div>
                    <div style={{ height: 6, background: colors.hairlineSoft, borderRadius: 3 }}>
                        <div
                            style={{
                                height: 6,
                                width: `${(item.value / max) * 100}%`,
                                background: CHART_SERIES[i % CHART_SERIES.length],
                                borderRadius: 3,
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

// ─── Coverage bar ───────────────────────────────────────────────

export const ScreenCoverageBar: React.FC<{ pct: number; label?: string }> = ({ pct, label }) => (
    <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: colors.muted }}>{label ?? 'Coverage'}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: colors.ink }}>{pct.toFixed(0)}%</span>
        </div>
        <div style={{ height: 6, background: colors.hairlineSoft, borderRadius: 3 }}>
            <div
                style={{
                    height: 6,
                    width: `${Math.max(0, Math.min(100, pct))}%`,
                    background: colors.brand,
                    borderRadius: 3,
                }}
            />
        </div>
    </div>
);

// ─── Donut ──────────────────────────────────────────────────────

export type DonutSlice = { label: string; value: number };

const buildDonutPaths = (slices: DonutSlice[], radius: number, hole: number) => {
    const total = slices.reduce((s, x) => s + Math.max(0, Number.isFinite(x.value) ? x.value : 0), 0) || 1;
    let angle = -Math.PI / 2;
    return slices.map((s, i) => {
        const sweep = (Math.max(0, s.value) / total) * Math.PI * 2;
        const start = angle;
        angle += sweep;
        const end = angle;
        const largeArc = sweep > Math.PI ? 1 : 0;
        const x1 = Math.cos(start) * radius;
        const y1 = Math.sin(start) * radius;
        const x2 = Math.cos(end) * radius;
        const y2 = Math.sin(end) * radius;
        const x3 = Math.cos(end) * hole;
        const y3 = Math.sin(end) * hole;
        const x4 = Math.cos(start) * hole;
        const y4 = Math.sin(start) * hole;
        const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${hole} ${hole} 0 ${largeArc} 0 ${x4} ${y4} Z`;
        return { d, color: CHART_SERIES[i % CHART_SERIES.length], label: s.label, pct: (Math.max(0, s.value) / total) * 100 };
    });
};

export const ScreenDonut: React.FC<{ slices: DonutSlice[]; size?: number }> = ({ slices, size = 100 }) => {
    const r = size / 2;
    const paths = buildDonutPaths(slices, r, r * 0.62);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <svg width={size} height={size} viewBox={`${-r} ${-r} ${size} ${size}`}>
                {paths.map((p, i) => (
                    <path key={i} d={p.d} fill={p.color} />
                ))}
            </svg>
            <div style={{ flex: 1 }}>
                {paths.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: 9,
                                height: 9,
                                background: p.color,
                                marginRight: 6,
                                borderRadius: 2,
                            }}
                        />
                        <span style={{ flex: 1, fontSize: 11.5, color: colors.inkSoft }}>{p.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: colors.ink }}>{p.pct.toFixed(1)}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Status dot row ────────────────────────────────────────────

export type StatusDot = { label: string; state: 'ok' | 'warn' | 'err' | 'idle' };

const DOT_COLORS = {
    ok: colors.pos,
    warn: colors.warn,
    err: colors.neg,
    idle: colors.mutedSoft,
};

export const ScreenStatusGrid: React.FC<{ items: StatusDot[]; cols?: number }> = ({ items, cols = 3 }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8, marginTop: 8 }}>
        {items.map((it, i) => (
            <div
                key={i}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 6,
                    borderRadius: 4,
                    background: colors.surfaceAlt,
                }}
            >
                <span
                    style={{
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        background: DOT_COLORS[it.state],
                        marginRight: 8,
                        display: 'inline-block',
                    }}
                />
                <span style={{ fontSize: 11.5, color: colors.inkSoft, flex: 1 }}>{it.label}</span>
            </div>
        ))}
    </div>
);

// ─── Line chart with axes ──────────────────────────────────────

export type LineChartPoint = { label: string; value: number };

export const ScreenLineChart: React.FC<{
    points: LineChartPoint[];
    height?: number;
    yLabel?: string;
}> = ({ points, height = 128, yLabel }) => {
    if (points.length < 2) return null;
    const values = points.map((p) => (Number.isFinite(p.value) ? p.value : 0));
    const max = Math.max(1, ...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const width = 600;
    const padTop = 12;
    const padBottom = 26;
    const padLeft = 44;
    const padRight = 10;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;
    const xStep = chartW / Math.max(1, points.length - 1);

    const pathPoints = points.map((p, i) => ({
        x: padLeft + i * xStep,
        y: padTop + chartH - ((p.value - min) / range) * chartH,
    }));
    const linePath = pathPoints.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L ${pathPoints[pathPoints.length - 1].x.toFixed(1)} ${padTop + chartH} L ${padLeft} ${padTop + chartH} Z`;

    const yTicks = 4;
    const ySteps = Array.from({ length: yTicks }, (_, i) => min + (range * i) / (yTicks - 1));
    const showEveryN = Math.max(1, Math.ceil(points.length / 8));

    return (
        <div style={{ width: '100%' }}>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
                {ySteps.map((yVal, i) => {
                    const y = padTop + chartH - ((yVal - min) / range) * chartH;
                    return (
                        <g key={`y-${i}`}>
                            <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke={colors.hairline} strokeWidth={0.5} />
                            <text
                                x={padLeft - 6}
                                y={y + 3}
                                fill={colors.ink40}
                                fontSize={8}
                                fontFamily="'Geist Mono', monospace"
                                textAnchor="end"
                            >
                                {yVal >= 1000 ? `${(yVal / 1000).toFixed(1)}k` : String(Math.round(yVal))}
                            </text>
                        </g>
                    );
                })}
                <path d={areaPath} fill={colors.brandSoft} opacity={0.4} />
                <path d={linePath} stroke={colors.brand} strokeWidth={2} fill="none" />
                {pathPoints.map((pt, i) => (
                    <circle key={i} cx={pt.x} cy={pt.y} r={2.5} fill={colors.brand} />
                ))}
                {points.map((p, i) =>
                    i % showEveryN === 0 || i === points.length - 1 ? (
                        <text
                            key={`x-${i}`}
                            x={pathPoints[i].x}
                            y={height - 4}
                            fill={colors.ink40}
                            fontSize={8}
                            fontFamily="'Geist Mono', monospace"
                            textAnchor="middle"
                        >
                            {p.label}
                        </text>
                    ) : null,
                )}
            </svg>
        </div>
    );
};
